import { CarvanaHeaderProvider } from '@carvana/header';
import dynamic from 'next/dynamic';
import React from 'react';
import { useSelector } from 'react-redux';
import { PortalContextProvider } from '@/common/Portal/PortalContext';
import ExperimentSetter from '@/vdp/ExperimentSetter';
import VehicleDetailsData from '@/vdp/VehicleDetailsContainer/VehicleDetailsData';
import {
  browserCookieIdExperiments,
  userIdExperiments
} from '~/constants/vdp/experiments';
import AnalyticsProvider from '~/context/common/AnalyticsProvider';
import {
  DeviceContextProvider,
  type OwnProps as DeviceContextProps
} from '~/context/common/DeviceContext';
import { DiscountContextProvider } from '~/context/common/DiscountContext';
import ExperimentProvider, {
  type PropsFromServer as ExperimentContextProps
} from '~/context/common/ExperimentProvider';
import { ShareContextProvider } from '~/context/common/ShareContext';
import {
  StaticContextProvider,
  type OwnProps as StaticContextProps
} from '~/context/common/StaticContext';
import TradeContext, {
  TradeContextProvider,
  type OwnProps as TradeContextProps
} from '~/context/common/TradeContext';
import {
  getAssociatedVdpEventId,
  getInventoryType,
  getSearchRequestId,
  getVehicleId
} from '~/selectors';
import { CarouselContextProvider } from './CarouselContext';
import { FinanceContextProvider } from './FinanceContext';
import FinanceData from './FinanceContext/FinanceData';
import { GlobalContextProvider } from './GlobalContext';
import { LocationContextProvider } from './LocationContext';
import { ModalContextProvider } from './ModalContext';
import {
  NativeContextProvider,
  type OwnProps as NativeContextProps
} from './NativeContext';
import { ShowcaseContextProvider } from './ShowcaseContext';
import { UnlockTimerContextProvider } from './UnlockTimerContext';
import { VehiclesAvailableSoonerContextProvider } from './VehiclesAvailableSoonerContext';
import { ErrorContextProvider } from '../common/ErrorContext';

const CoreAuth = dynamic(
  () =>
    import(
      /* webpackChunkName: "CoreAuth" */ '@/vdp/VehicleDetailsContainer/CoreAuth'
    ),
  { ssr: false }
);

export type VehicleDetailsProvidersProps = {
  forDeviceContext: DeviceContextProps;
  forExperimentContext: ExperimentContextProps;
  forNativeContext: NativeContextProps;
  forStaticContext: StaticContextProps;
  forTradeContext: TradeContextProps;
};

type OwnProps = React.PropsWithChildren<
  VehicleDetailsProvidersProps & {
    eventableProject: string;
    eventableTrack: string;
  }
>;

export default function VehicleDetailsProviders({
  children,
  eventableProject,
  eventableTrack,
  forDeviceContext,
  forExperimentContext,
  forNativeContext,
  forStaticContext,
  forTradeContext
}: OwnProps) {
  const inventoryType = useSelector(getInventoryType);
  const associatedVdpEventId = useSelector(getAssociatedVdpEventId);
  const searchRequestId = useSelector(getSearchRequestId);
  const vehicleId = useSelector(getVehicleId);

  return (
    <StaticContextProvider {...forStaticContext}>
      <NativeContextProvider {...forNativeContext}>
        <ExperimentProvider
          {...forExperimentContext}
          browserCookieIdExperiments={browserCookieIdExperiments}
          userIdExperiments={userIdExperiments}
        >
          <ExperimentSetter />
          <CarvanaHeaderProvider>
            <AnalyticsProvider
              correlationId={associatedVdpEventId}
              payloadMetaAttributes={{
                inventoryType,
                searchRequestId,
                vehicleId
              }}
              projectName={eventableProject}
              track={eventableTrack}
            >
              <DeviceContextProvider {...forDeviceContext}>
                <ErrorContextProvider>
                  <GlobalContextProvider>
                    <FinanceContextProvider>
                      <TradeContextProvider {...forTradeContext}>
                        <TradeContext.Consumer>
                          {({ activeTrade, isTradeInApplied }) => (
                            <DiscountContextProvider
                              hasActiveTrade={Boolean(activeTrade)}
                              isTradeInApplied={isTradeInApplied}
                            >
                              <FinanceData />
                              <PortalContextProvider>
                                <ShareContextProvider>
                                  <ModalContextProvider>
                                    <UnlockTimerContextProvider>
                                      <LocationContextProvider>
                                        <VehiclesAvailableSoonerContextProvider>
                                          <CarouselContextProvider>
                                            <ShowcaseContextProvider>
                                              <CoreAuth />
                                              <VehicleDetailsData />
                                              {children}
                                            </ShowcaseContextProvider>
                                          </CarouselContextProvider>
                                        </VehiclesAvailableSoonerContextProvider>
                                      </LocationContextProvider>
                                    </UnlockTimerContextProvider>
                                  </ModalContextProvider>
                                </ShareContextProvider>
                              </PortalContextProvider>
                            </DiscountContextProvider>
                          )}
                        </TradeContext.Consumer>
                      </TradeContextProvider>
                    </FinanceContextProvider>
                  </GlobalContextProvider>
                </ErrorContextProvider>
              </DeviceContextProvider>
            </AnalyticsProvider>
          </CarvanaHeaderProvider>
        </ExperimentProvider>
      </NativeContextProvider>
    </StaticContextProvider>
  );
}
